import { MatterData, MatterDocument } from '@shared/types';
import { OrderByClause, WhereClause } from '../queryBuilder';
import { useEffect, useMemo } from 'react';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import { LoadState, makeLoadState, useLoadState } from '@/hooks/useLoadState';

export const useMatters = (options?: {
  where?: WhereClause<MatterData>[];
  orderBy?: OrderByClause<MatterData>[];
  disabled?: boolean;
}) => {
  const authSession = useAuthenticatedSession();
  const { loadState, setLoadState } = useLoadState<MatterDocument[]>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({
      status: 'loading',
    });
    if (!authSession) {
      return;
    }
    if (options?.disabled === true) {
      return;
    }
    const where = options?.where ? [...options.where] : [];
    const orderBy = options?.orderBy ? [...options.orderBy] : [];
    if (orderBy.length === 0) {
      orderBy.push(['name', 'asc']);
    }
    return authSession.db.subscribeToMattersChanges(
      async (collectionSnapshot) => {
        const docs = collectionSnapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          return { ...data, id: docSnapshot.id } as MatterDocument;
        });

        setLoadState({ status: 'loaded', data: docs });
      },
      where,
      orderBy,
    );
  }, [authSession, JSON.stringify(options)]);

  return loadState;
};

export const useMattersById = (options?: {
  where?: WhereClause<MatterData>[];
  orderBy?: OrderByClause<MatterData>[];
}): LoadState<Record<string, MatterDocument>> => {
  const mattersLoadState = useMatters(options);
  return useMemo(() => {
    if (mattersLoadState.status === 'loading') {
      return makeLoadState({ status: 'loading' });
    }
    if (mattersLoadState.status === 'error') {
      return makeLoadState({ status: 'error', error: mattersLoadState.error });
    }
    return makeLoadState({
      status: 'loaded',
      data: mattersLoadState.data.reduce(
        (acc, matter) => {
          acc[matter.id] = matter;
          return acc;
        },
        {} as Record<string, MatterDocument>,
      ),
    });
  }, [mattersLoadState.snapshotId]);
};

export function useMatter(id: string | undefined) {
  const { loadState, setLoadState } = useLoadState<MatterDocument>({
    status: 'loading',
  });
  const authSession = useAuthenticatedSession();
  useEffect(() => {
    setLoadState({ status: 'loading' });

    if (!authSession) {
      return;
    }
    if (!id) {
      return;
    }
    return authSession.db.subscribeToMatterChanges(id, (snapshot) => {
      setLoadState({ status: 'loading' });

      const data = snapshot.data();
      if (!data) {
        setLoadState({
          status: 'error',
          error: new Error('Matter not found'),
        });
        return;
      }
      setLoadState({ status: 'loaded', data: { ...data, id: snapshot.id } });
    });
  }, [authSession, id]);
  return loadState;
}
